export interface SpeakOptions {
  voiceURI?: string | null;
  pitch?: number;
  rate?: number;
  volume?: number;
  signal?: AbortSignal;
}

export type SpeechStatus = 'idle' | 'speaking' | 'paused';

export class SpeechUnavailableError extends Error {
  constructor() {
    super('This browser does not support speech synthesis.');
    this.name = 'SpeechUnavailableError';
  }
}

const KEEPALIVE_INTERVAL_MS = 10_000;
const VOICE_POLL_INTERVAL_MS = 100;
const VOICE_POLL_TIMEOUT_MS = 5000;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Promise-based wrapper over the Web Speech API.
 *
 * Works around three platform bugs that break naive implementations:
 *  - `getVoices()` returns an empty list until the async `voiceschanged` event.
 *  - Chrome silently stops speaking after ~15s unless pause/resume is pumped.
 *  - Utterances garbage-collected mid-speech never fire `onend`, so live
 *    utterances are held in a set until they settle.
 */
export class SpeechEngine {
  private readonly synth: SpeechSynthesis | null;
  private voices: SpeechSynthesisVoice[] = [];
  private voicesPromise: Promise<SpeechSynthesisVoice[]> | null = null;
  private readonly pending = new Set<SpeechSynthesisUtterance>();
  private keepalive: ReturnType<typeof setInterval> | null = null;
  private primed = false;
  private status: SpeechStatus = 'idle';
  private readonly listeners = new Set<(status: SpeechStatus) => void>();

  constructor(synth: SpeechSynthesis | null = globalThis.speechSynthesis ?? null) {
    this.synth = synth ?? null;
  }

  get supported(): boolean {
    return this.synth !== null;
  }

  getStatus(): SpeechStatus {
    return this.status;
  }

  subscribe(listener: (status: SpeechStatus) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setStatus(status: SpeechStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const listener of this.listeners) listener(status);
  }

  /**
   * iOS Safari only permits speech that originates in a user gesture, so the
   * first tap of a rehearsal must call this to unlock the audio channel.
   */
  prime(): void {
    if (!this.synth || this.primed) return;
    this.primed = true;
    const silent = new SpeechSynthesisUtterance('');
    silent.volume = 0;
    try {
      this.synth.speak(silent);
      this.synth.cancel();
    } catch {
      this.primed = false;
    }
  }

  get isPrimed(): boolean {
    return this.primed;
  }

  async loadVoices(): Promise<SpeechSynthesisVoice[]> {
    if (!this.synth) return [];
    if (this.voices.length > 0) return this.voices;
    this.voicesPromise ??= this.pollForVoices();
    return this.voicesPromise;
  }

  private pollForVoices(): Promise<SpeechSynthesisVoice[]> {
    const synth = this.synth;
    if (!synth) return Promise.resolve([]);

    return new Promise((resolve) => {
      const settle = () => {
        const list = synth.getVoices();
        if (list.length === 0) return false;
        this.voices = list;
        cleanup();
        resolve(list);
        return true;
      };

      const poll = setInterval(settle, VOICE_POLL_INTERVAL_MS);
      const timeout = setTimeout(() => {
        cleanup();
        resolve(synth.getVoices());
      }, VOICE_POLL_TIMEOUT_MS);

      const cleanup = () => {
        clearInterval(poll);
        clearTimeout(timeout);
        synth.removeEventListener?.('voiceschanged', settle);
      };

      synth.addEventListener?.('voiceschanged', settle);
      settle();
    });
  }

  getLoadedVoices(): SpeechSynthesisVoice[] {
    return this.voices;
  }

  speak(text: string, options: SpeakOptions = {}): Promise<void> {
    const synth = this.synth;
    if (!synth) return Promise.reject(new SpeechUnavailableError());
    if (options.signal?.aborted) return Promise.resolve();

    const body = text.trim();
    if (body === '') return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(body);
      const voice = options.voiceURI ? this.voices.find((v) => v.voiceURI === options.voiceURI) : null;
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang;
      }
      utterance.pitch = clamp(options.pitch ?? 1, 0, 2);
      utterance.rate = clamp(options.rate ?? 1, 0.1, 10);
      utterance.volume = clamp(options.volume ?? 1, 0, 1);

      let settled = false;
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        this.pending.delete(utterance);
        options.signal?.removeEventListener('abort', onAbort);
        if (this.pending.size === 0) {
          this.stopKeepalive();
          this.setStatus('idle');
        }
        fn();
      };

      const onAbort = () => {
        synth.cancel();
        finish(resolve);
      };

      utterance.onend = () => finish(resolve);
      utterance.onerror = (event) => {
        // A cancel/interrupt is a normal control-flow outcome, not a failure.
        if (event.error === 'canceled' || event.error === 'interrupted') {
          finish(resolve);
        } else {
          finish(() => reject(new Error(`Speech failed: ${event.error}`)));
        }
      };

      options.signal?.addEventListener('abort', onAbort, { once: true });
      this.pending.add(utterance);
      this.startKeepalive();
      this.setStatus('speaking');
      synth.speak(utterance);
    });
  }

  cancel(): void {
    if (!this.synth) return;
    this.synth.cancel();
    this.stopKeepalive();
    this.setStatus('idle');
  }

  pause(): void {
    if (!this.synth || this.status !== 'speaking') return;
    this.synth.pause();
    this.stopKeepalive();
    this.setStatus('paused');
  }

  resume(): void {
    if (!this.synth || this.status !== 'paused') return;
    this.synth.resume();
    this.startKeepalive();
    this.setStatus('speaking');
  }

  private startKeepalive(): void {
    if (this.keepalive !== null || !this.synth) return;
    const synth = this.synth;
    this.keepalive = setInterval(() => {
      if (synth.speaking && !synth.paused) {
        synth.pause();
        synth.resume();
      }
    }, KEEPALIVE_INTERVAL_MS);
  }

  private stopKeepalive(): void {
    if (this.keepalive === null) return;
    clearInterval(this.keepalive);
    this.keepalive = null;
  }

  dispose(): void {
    this.cancel();
    this.listeners.clear();
    this.pending.clear();
  }
}

export const speechEngine = new SpeechEngine();

/** Silence helper that honours cancellation, used for inter-line pauses. */
export function wait(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    }
    signal?.addEventListener('abort', done, { once: true });
  });
}
